/**
 * Host tree — rendered iteratively (flat list with indentation) to avoid
 * self-referential component recursion, which trips the visual-edits
 * babel plugin's binding traversal into a stack overflow.
 */
import { Trash2, Lock } from "lucide-react";

function displayName(it) {
  return it.label || it.type;
}

// Flatten the tree into an ordered list of {item, depth}.
function flatten(rootIds, byHost, items, acc, depth) {
  for (const id of rootIds) {
    const it = items.find((i) => i.id === id);
    if (!it) continue;
    acc.push({ it, depth });
    const kids = (byHost[it.id] || []).map((k) => k.id);
    if (kids.length) flatten(kids, byHost, items, acc, depth + 1);
  }
}

export function HostTreeList({ items, byHost, rootIds, selectedId, visualLayerOf, onSelect, onDelete }) {
  const flat = [];
  flatten(rootIds, byHost, items, flat, 0);
  return (
    <ul className="space-y-1" data-testid="items-list-host">
      {flat.map(({ it, depth }) => (
        <FlatRow key={it.id} it={it} depth={depth}
                 hostItem={items.find((h) => h.id === it.host_id)}
                 selected={it.id === selectedId}
                 visualLayer={visualLayerOf(it.id)}
                 onSelect={onSelect} onDelete={onDelete}/>
      ))}
    </ul>
  );
}

function FlatRow({ it, depth, hostItem, selected, visualLayer, onSelect, onDelete }) {
  return (
    <li style={{ paddingLeft: depth * 12 }}>
      <div data-row-id={it.id} data-testid={`items-row-${it.id}`}
           aria-selected={selected}
           className={`group flex items-center gap-2 border border-transparent px-2 py-2 min-h-[44px] cursor-pointer transition-colors ${selected ? "bg-muted border-foreground" : "hover:border-border hover:bg-muted/50"}`}
           onClick={() => onSelect(it.id)}>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-medium truncate">{displayName(it)}</div>
          <div className="text-[10px] text-muted-foreground truncate flex items-center gap-2">
            <span>Host: {hostItem ? displayName(hostItem) : "Wall"}</span>
            <span>·</span>
            <span>Visual: {visualLayer ?? "—"}</span>
            <span>·</span>
            <span>Assembly: {it.assembly_order ?? "—"}</span>
          </div>
        </div>
        {it.locked && (
          <span className="text-foreground/70" title="Locked">
            <Lock className="w-3 h-3" strokeWidth={1.5}/>
          </span>
        )}
        <button type="button" data-testid={`items-row-delete-${it.id}`}
                disabled={it.locked}
                title={it.locked ? "Unlock to delete" : "Delete"}
                onClick={(e) => { e.stopPropagation(); onDelete(it); }}
                className="text-muted-foreground hover:text-[color:hsl(var(--destructive))] disabled:opacity-30 min-w-[32px] min-h-[32px] flex items-center justify-center">
          <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
        </button>
      </div>
    </li>
  );
}
